// Detail-Snapshot fuer /infra/storage. Liefert die volle Storage-Shape
// (pools/datasets/disks/filesystems) + die Monitor-Liste -- das was
// die Page rendert. Aggregat-Status fuer die Dashboard-Card rechnet
// storage-summary aus diesem Store (kein eigener Abruf).
//
// Live-Update (Realtime-Mandat) ueber das Homelab-Protokoll (docs/protokoll.md
// › „Speicher über das Protokoll“, seit 02.11.2026): jeder Pool, jede Platte,
// jedes Dataset und jedes Dateisystem ist eine Instanz (speicher.pool.<id>,
// .platte., .dataset., .dateisystem.), dazu speicher.lage (letzter Lauf,
// Standard-Schwelle). Bis dahin holte der Store die ganze Antwort alle 30 s
// und bei jedem WS-Signal "storage" neu (29 kB je Abruf, je Tab zwei Stores
// mit eigenem Takt); je Runde aendern sich aber nur ein paar Pools, Datasets
// und Platten.
//   - REST `/v2-api/storage` beim Start, als Revalidator und in
//     refreshStorageDetails (nach einer Aktion, deren Ergebnis die Seite
//     sofort zeigen soll). KEIN Takt.
//   - `speicher.<art>.*` (Rate normal): Snapshot + Delta je Eintrag. Ein Stand
//     aktualisiert den Eintrag in der Liste in-place (Objekt-Identitaet und
//     Reihenfolge bleiben), ein unbekannter haengt sich hinten an, `weg`
//     nimmt ihn heraus. Der Instanzname ist ein Hash; den Eintrag findet der
//     Store ueber host+name aus dem Stand (merkt sich Instanz -> Schluessel,
//     denn `weg` kennt nur den Instanznamen). Der juengste Stand je Eintrag
//     wird gemerkt; kam er waehrend eines REST-Abrufs, liegt er ueber dessen
//     Antwort (sie kann dahinter liegen), aeltere nicht.
//   - Monitore: REST beim Start und als Revalidator; geaendert wird die Liste
//     nur ueber die Einstellungen -- deren Mutationen meldet die Middleware
//     auf dem WS-Kanal "settings" (lib/settings-live.ts). Kein Takt.
// Der alte WS-Kanal "storage" bleibt (DatasetWerkzeuge.svelte laedt darauf die
// Snapshots des offenen Datasets nach).
//
// SWR: ja -- Page rendert beim Reload sofort den letzten Pool/Dataset-
// Baum statt "lade...".

import { getJSON } from "./api";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { SpeicherDateisystem, SpeicherDataset, SpeicherLage, SpeicherPlatte, SpeicherPool } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import { onSettingsChange } from "./settings-live";
import {
  artAusInstanz,
  datasetAusProtokoll,
  dateisystemAusProtokoll,
  gleich,
  lageAusProtokoll,
  mitStaenden,
  platteAusProtokoll,
  poolAusProtokoll,
  speicherSchluessel,
  uebernimm,
  type Gemerkt,
  type Lage,
  type SpeicherArt,
} from "./storage-protokoll";
import type { Disk, Filesystem, Storage, ZfsDataset, ZfsPool } from "./types";

export interface StorageMonitor {
  id: number;
  dataset: string;
  host: string;
  warn_pct: number;
  crit_pct: number;
  auto_delete_snapshots: boolean;
  keep_snapshots: number;
  position: number;
  recursive: boolean;
}

const KEY_STORAGE = "storage-details";
const KEY_MONITORS = "storage-monitors";

type Eintrag = ZfsPool | Disk | ZfsDataset | Filesystem;
type ListenName = "pools" | "disks" | "datasets" | "filesystems";

// Je Art: Topic-Familie (fuer skaliere), Liste im Storage, Schluessel eines
// Eintrags (host+name, bei Dateisystemen der Mountpoint -- wie
// adapter.SpeicherId ihn hasht) und die Umrechnung Stand -> REST-Form.
interface ArtInfo {
  familie: string;
  liste: ListenName;
  schluessel: (e: never) => string;
  aus: (daten: Record<string, unknown>) => Eintrag;
}

const ARTEN: Record<SpeicherArt, ArtInfo> = {
  pool: {
    familie: "speicher.pool",
    liste: "pools",
    schluessel: (e: ZfsPool) => speicherSchluessel(e.host, e.name),
    aus: (d) => poolAusProtokoll(skaliere<SpeicherPool>("speicher.pool", d)),
  },
  platte: {
    familie: "speicher.platte",
    liste: "disks",
    schluessel: (e: Disk) => speicherSchluessel(e.host, e.name),
    aus: (d) => platteAusProtokoll(skaliere<SpeicherPlatte>("speicher.platte", d)),
  },
  dataset: {
    familie: "speicher.dataset",
    liste: "datasets",
    schluessel: (e: ZfsDataset) => speicherSchluessel(e.host, e.name),
    aus: (d) => datasetAusProtokoll(skaliere<SpeicherDataset>("speicher.dataset", d)),
  },
  dateisystem: {
    familie: "speicher.dateisystem",
    liste: "filesystems",
    schluessel: (e: Filesystem) => speicherSchluessel(e.host, e.mountpoint),
    aus: (d) => dateisystemAusProtokoll(skaliere<SpeicherDateisystem>("speicher.dateisystem", d)),
  },
};
const ALLE_ARTEN = Object.keys(ARTEN) as SpeicherArt[];

function schluesselVon(art: SpeicherArt, e: Eintrag): string {
  return (ARTEN[art].schluessel as (e: Eintrag) => string)(e);
}

// Tief reaktiv: Seiten lesen Felder, ein Stand aendert nur die betroffenen.
let _daten = $state<Storage | undefined>(undefined);
let _monitors = $state<StorageMonitor[] | undefined>(undefined);
let _started = false;
let _laedt = true;
let _unsub: Array<() => void> = [];

// Der juengste Protokoll-Stand je Eintrag, mit laufender Nummer des
// Eintreffens: ein Abruf legt nur Staende ueber seine Antwort, die nach seinem
// Start kamen -- aeltere hat der Server beim Bauen der Antwort schon gekannt
// (und nach einer Unterbrechung des Protokolls waeren sie veraltet). `e: null`
// = der Eintrag ist weg: eine Antwort, die ihn noch nennt, holt ihn nicht
// zurueck.
const _merk: Record<SpeicherArt, Map<string, Gemerkt<Eintrag>>> = {
  pool: new Map(),
  platte: new Map(),
  dataset: new Map(),
  dateisystem: new Map(),
};
let _lage: Gemerkt<Lage> | undefined;
let _nr = 0;
// Instanzname -> Schluessel: `weg` nennt nur die Instanz.
const _schluessel = new Map<string, string>();

// ── Persistenz ─────────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (jede Runde ein paar Eintraege):
// gespeichert wird gebuendelt, hoechstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
const _beiPersist = new Set<() => void>();
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    if (_daten) persist(KEY_STORAGE, $state.snapshot(_daten));
    for (const fn of _beiPersist) fn();
  }, 2_000);
}

/** Meldet `fn` an, wenn der Store seinen Stand gerade gesichert hat (hoechstens
 *  alle 2 s) -- storage-summary sichert darin die Karte fuer den Kaltstart. */
export function beiStorageGesichert(fn: () => void): () => void {
  _beiPersist.add(fn);
  return () => _beiPersist.delete(fn);
}

// ── Staende ────────────────────────────────────────────────────────────

function liste(d: Storage, name: ListenName): Eintrag[] {
  return (d[name] ??= []) as Eintrag[];
}

function beiStand(art: SpeicherArt, instanz: string, daten: Record<string, unknown>): void {
  const info = ARTEN[art];
  const neu = info.aus(daten);
  const k = schluesselVon(art, neu);
  _schluessel.set(instanz, k);
  _merk[art].set(k, { e: neu, nr: ++_nr });
  // Noch keine Liste (weder gesichert noch abgerufen): der Abruf legt den
  // gemerkten Stand ueber die Antwort.
  if (!_daten) return;
  const l = liste(_daten, info.liste);
  const ex = l.find((x) => schluesselVon(art, x) === k);
  if (ex) {
    if (uebernimm(ex, neu)) persistSpaeter();
  } else if (!_laedt) {
    // Ein Eintrag, den die Liste noch nicht kennt (eben dazugekommen). Vor
    // dem ersten Abruf legt load() den Stand ueber die Antwort.
    l.push(neu);
    persistSpaeter();
  }
}

function beiWeg(art: SpeicherArt, instanz: string): void {
  const k = _schluessel.get(instanz);
  if (k === undefined) return;
  _schluessel.delete(instanz);
  _merk[art].set(k, { e: null, nr: ++_nr });
  if (!_daten) return;
  const l = liste(_daten, ARTEN[art].liste);
  const i = l.findIndex((x) => schluesselVon(art, x) === k);
  if (i >= 0) {
    l.splice(i, 1);
    persistSpaeter();
  }
}

// Die Felder der Antwort ausserhalb der Listen. Fehlendes (last_refresh vor
// dem ersten Lauf) wird am Ziel entfernt.
type LageFelder = Pick<Storage, "last_refresh" | "last_refresh_ok" | "schwelle_standard">;
const LAGE_FELDER = ["last_refresh", "last_refresh_ok", "schwelle_standard"] as const;

function setzeLage(d: Storage, l: LageFelder): boolean {
  let geaendert = false;
  for (const k of LAGE_FELDER) {
    if (l[k] === undefined) {
      if (k in d) {
        delete d[k];
        geaendert = true;
      }
    } else if (!gleich(d[k], l[k])) {
      (d as unknown as Record<string, unknown>)[k] = l[k];
      geaendert = true;
    }
  }
  return geaendert;
}

function beiLage(daten: Record<string, unknown>): void {
  const l = lageAusProtokoll(skaliere<SpeicherLage>("speicher.lage", daten));
  _lage = { e: l, nr: ++_nr };
  if (_daten && setzeLage(_daten, l)) persistSpaeter();
}

// ── REST ───────────────────────────────────────────────────────────────

// Gab es seit `ab` etwas aus dem Protokoll?
function neuereStaende(ab: number): boolean {
  return (_lage?.nr ?? 0) > ab || ALLE_ARTEN.some((a) => [..._merk[a].values()].some((g) => g.nr > ab));
}

// applyStorage merged die frische Antwort IN-PLACE in den bestehenden Store:
// vorhandene Eintraege behalten Objekt-Identitaet (nur Felder werden
// aktualisiert), die Reihenfolge der Antwort gilt, Neue haengen sich hinten
// an. So kein Voll-Re-Render der Tabellen bei jedem Wert.
// `rest` fehlt, wenn der Abruf scheiterte: dann gilt, was da ist, plus was das
// Protokoll waehrenddessen brachte.
function applyStorage(rest: Storage | undefined, ab: number): void {
  if (!_daten) {
    // Ohne Antwort und ohne Protokoll gibt es nichts zu zeigen.
    if (!rest && !neuereStaende(ab)) return;
    _daten = { pools: [], datasets: [], disks: [], filesystems: [] };
  }
  const d = _daten;
  for (const art of ALLE_ARTEN) {
    const info = ARTEN[art];
    const cur = liste(d, info.liste);
    const eingang = rest ? ((rest[info.liste] as Eintrag[] | undefined) ?? []) : cur;
    const alle = mitStaenden(eingang, _merk[art], ab, (e) => schluesselVon(art, e));
    const byKey = new Map(cur.map((e) => [schluesselVon(art, e), e]));
    const next = alle.map((e) => {
      const ex = byKey.get(schluesselVon(art, e));
      if (ex) {
        uebernimm(ex, e);
        return ex;
      }
      return e;
    });
    const same = next.length === cur.length && next.every((e, i) => e === cur[i]);
    if (!same) d[info.liste] = next as never;
  }
  if (rest) setzeLage(d, (_lage?.nr ?? 0) > ab ? _lage!.e! : rest);
  else if (_lage && _lage.nr > ab) setzeLage(d, _lage.e!);
  persistSpaeter();
}

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt kaeme sonst
// erst der naechste Revalidator -- und bis dahin bliebe ein aus der Ablage
// geholter, inzwischen geloeschter Eintrag stehen (fuer ihn kommt kein `weg`,
// diese Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

async function ladeStorage(): Promise<void> {
  const ab = _nr;
  try {
    applyStorage(await getJSON<Storage>("/v2-api/storage"), ab);
  } catch {
    // Was das Protokoll waehrend des Abrufs gebracht hat, gilt trotzdem --
    // beim ersten Abruf waere die Liste sonst leer, bis jeder Eintrag sich
    // aendert.
    applyStorage(undefined, ab);
    if (_started && !_neuversuch) {
      _neuversuch = setTimeout(() => {
        _neuversuch = undefined;
        if (_started) void ladeStorage();
      }, NEUVERSUCH_MS);
    }
  } finally {
    _laedt = false;
  }
}

async function ladeMonitore(): Promise<void> {
  try {
    const list = await getJSON<StorageMonitor[]>("/v2-api/settings/storage-monitors");
    if (!gleich(list, _monitors)) _monitors = list;
    persist(KEY_MONITORS, list);
  } catch {
    // ignore -- der naechste Anlass versucht's wieder
  }
}

function refresh(): Promise<void> {
  return Promise.all([ladeStorage(), ladeMonitore()]).then(() => undefined);
}

export function startStorageDetails(): void {
  if (_started) return;
  _started = true;
  _laedt = true;
  const cachedStorage = loadPersisted<Storage>(KEY_STORAGE);
  if (cachedStorage) _daten = cachedStorage;
  const cachedMonitors = loadPersisted<StorageMonitor[]>(KEY_MONITORS);
  if (cachedMonitors) _monitors = cachedMonitors;
  void refresh();
  for (const art of ALLE_ARTEN) {
    _unsub.push(
      abonniere(
        `${ARTEN[art].familie}.*`,
        { rate: "normal" },
        {
          stand: (instanz, daten) => {
            if (artAusInstanz(instanz)?.art === art) beiStand(art, instanz, daten);
          },
          weg: (instanz) => {
            if (artAusInstanz(instanz)?.art === art) beiWeg(art, instanz);
          },
        },
      ),
    );
  }
  _unsub.push(abonniere("speicher.lage", { rate: "normal" }, { stand: (_instanz, daten) => beiLage(daten) }));
  // Monitore aendern sich nur ueber die Einstellungen: die Middleware meldet
  // jede Mutation unter /v2-api/settings/ auf dem Kanal "settings".
  _unsub.push(onSettingsChange(["/v2-api/settings/storage-monitors"], ladeMonitore));
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reisst die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Loeschungen und Aenderungen der
  // Monitore fuer immer weg. registerRevalidator holt beim Reconnect und beim
  // Zurueckkommen des Tabs alles frisch. (Der Protokoll-Client holt sich nach
  // einer Luecke selbst einen Snapshot je Instanz; die Revalidation deckt
  // zusaetzlich Eintraege ab, die waehrend der Luecke verschwanden.)
  _unsub.push(registerRevalidator(refresh));
}

export function stopStorageDetails(): void {
  _started = false;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _daten = undefined;
  _monitors = undefined;
  _lage = undefined;
  for (const a of ALLE_ARTEN) _merk[a].clear();
  _schluessel.clear();
}

export function getStorageDetails(): Storage | undefined {
  return _daten;
}

export function getStorageMonitors(): StorageMonitor[] | undefined {
  return _monitors;
}

export function refreshStorageDetails(): Promise<void> {
  return refresh();
}
